import React, { useEffect, useState, useId } from "react";
import useDialogFocus, { isTopDialog } from "../hooks/useDialogFocus";
import Draggable from "react-draggable";
import { RxCross1 } from "react-icons/rx";
import { IoVolumeHigh, IoVolumeMute } from "react-icons/io5";
import { MdAccessTime } from "react-icons/md";
import Swal from "sweetalert2";
import ScreenLoader from "./ScreenLoader";
import { useSelector } from "react-redux";

const modalSizes = {
  xl: "w-[800px]",
  xxl: "sm:w-[800px] md:w-[1140px]",
  xxxl: "md:w-[90%] sm:w-[800px]",
  viewfile: "sm:w-[800px] md:w-[1140px] xxxl:w-[1240px]",
  chat: "md:w-[80%] sm:w-[800px] h-[calc(100vh-112px)]",
  sm: "w-[500px]",
};

const CustomModal = ({
  isLoading,
  title,
  ModalBody,
  size,
  onClose,
  isDraggable = false,
  // Opt-in for forms that must fill the space left below the modal header.
  fillAvailableHeight = false,
  // Opt-in, and off by default on purpose: a modal holding a half-filled form
  // (the ticket editor) must not be dismissable by a stray Escape or a click
  // beside it. Read-only modals such as the file viewer turn these on so there
  // is always a way out.
  closeOnEsc = false,
  closeOnBackdrop = false,
}) => {
  const dialogRef = useDialogFocus();
  const titleId = useId();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMuted, setIsMuted] = useState(false);
  const connection = useSelector((state) => state.socket.socketConnection);
  // `socketConnection` starts as null (see SocketSlice) and is only set once
  // initiateSignalR resolves, so this must be optional-chained — CustomModal
  // is shared by every screen in the app, and reading `.state` off null here
  // would throw and blank out any modal opened before the socket connects.
  const isConnected = connection?.state === "Connected";

  useEffect(() => {
    const savedMuteState = localStorage.getItem("chatSoundMuted");
    if (savedMuteState !== null) {
      setIsMuted(JSON.parse(savedMuteState));
    }
  }, []);

  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [size]);

  useEffect(() => {
    if (!closeOnEsc || !onClose) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isTopDialog(dialogRef.current) && !document.activeElement?.closest(".swal2-popup")) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeOnEsc, onClose, dialogRef]);

  // Only a click on the backdrop itself closes - never one that bubbled up
  // from inside the modal.
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose?.();
  };

  const toggleMute = () => {
    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    // Save to localStorage
    localStorage.setItem("chatSoundMuted", JSON.stringify(newMuteState));

    // Optional: You can add sound feedback here
    if (!newMuteState) {
      // Play a subtle sound when unmuting
      playUnmuteSound();
    }
  };

  const playUnmuteSound = () => {
    // Simple beep sound when unmuting (optional)
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.1
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log("Audio context not supported");
    }
  };

  const handleDisappearingChatsClick = () => {
    Swal.fire({
      title: "Auto-Disappearing Chats",
      html: "<p>Only chats from the last 2 months are kept.</p><p>Older chats will be automatically removed.</p>",
      icon: "info",
      confirmButtonText: "OK",
      confirmButtonColor: "#3B82F6",
    });
  };

  const isChatModal = title === "Chat";

  return (
    <div
      onClick={closeOnBackdrop ? handleBackdropClick : undefined}
      className="pc-modal-backdrop min-w-full min-h-screen flex justify-center items-center fixed
        top-0 left-0 bg-gray-600/90 z-10"
    >
      {isLoading && <ScreenLoader />}

      <Draggable
        handle=".drag-handle"
        position={position}
        disabled={!isDraggable}
        onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`app-modal bg-white flex flex-col ${modalSizes[size]} rounded-t-xl ${
            fillAvailableHeight ? "h-[94dvh]" : "max-h-[94vh]"
          }`}
        >
          {/* Header */}
          <div
            className={`bg-sideNavbg p-3 flex shrink-0 justify-between items-center rounded-t-xl drag-handle px-5 ${
              isDraggable && "cursor-move"
            }`}
          >
            <div className="flex items-center">
              <span
                id={titleId}
                className={`relative text-md text-white font-semibold px-3 ml-5 before:content-[''] before:w-3 before:h-3 before:rounded-full before:absolute before:top-[5px] before:-left-2 ${
                  isChatModal && isConnected
                    ? "before:bg-green-500"
                    : isChatModal && !isConnected
                    ? "before:bg-red-500"
                    : "before:bg-white"
                }`}
                title={
                  isChatModal && isConnected
                    ? "You are Online"
                    : isChatModal && !isConnected
                    ? "You are Offline, Get Reconnected from the app header"
                    : title
                }
              >
                {title}
              </span>

              {/* Sound Mute/Unmute Button - Only for Chat modal */}
              {isChatModal && (
                <>
                  <button
                    className="ml-1 p-1 rounded-full hover:bg-gray-600/30 transition-colors"
                    onClick={handleDisappearingChatsClick}
                    aria-label="Auto-disappearing chats"
                    title="Auto-disappearing chats"
                  >
                    <MdAccessTime className="w-4 h-4 text-white" />
                  </button>
                  <button
                    className="ml-1 p-1 rounded-full hover:bg-gray-600/30 transition-colors"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute sound" : "Mute sound"}
                    title={isMuted ? "Unmute sound" : "Mute sound"}
                  >
                    {isMuted ? (
                      <IoVolumeMute className="w-4 h-4 text-white" />
                    ) : (
                      <IoVolumeHigh className="w-4 h-4 text-white" />
                    )}
                  </button>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="cursor-pointer flex items-center justify-center rounded p-1 text-lg text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <RxCross1 />
            </button>
          </div>

          {/* Body */}
          <div
            className={
              fillAvailableHeight || isChatModal
                ? "bg-white flex flex-1 min-h-0 overflow-hidden"
                : "bg-white overflow-auto"
            }
          >
            {ModalBody}
          </div>
        </div>
      </Draggable>
    </div>
  );
};

export default CustomModal;
